import type { AnalyticsSpend } from "@server/types/analyticsReportTypes";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { formatAtoms } from "@/components/billing/billing-format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { formatXAxisLabel, type Granularity } from "../utils/formatMetric";

const config = {
  amountUsd: { label: "Settled usage", color: "var(--chart-1)" },
};
const unavailable = {
  admin_required: "Spend is available to organization admins.",
  request_filters:
    "Clear API key, request type and status filters to see spend.",
  not_supported: "Spend is unavailable for this billing account.",
  read_failed: "Unable to load spend. Try refreshing.",
  range_too_large: "Select a shorter date range to see spend.",
};

/** Shows actual posted prepaid charges; exact amounts remain strings in tooltips while bars use numeric coordinates. */
export function SpendChart({
  spend,
  granularity,
}: {
  spend: AnalyticsSpend | undefined;
  granularity: Granularity;
}) {
  const data =
    spend?.status === "ready"
      ? spend.timeseries.map((point) => ({
          ...point,
          amountUsd: Number(point.amountAtoms) / 1e14,
        }))
      : [];
  return (
    <Card className="analytics-panel">
      <CardHeader className="analytics-chart-heading">
        <CardTitle>Spend</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="analytics-legend">
          <span>
            <i style={{ background: "var(--chart-1)" }} />
            Settled usage · USD
          </span>
        </div>
        {spend?.status === "ready" ? (
          <ChartContainer
            config={config}
            className="h-[200px] w-full aspect-auto"
          >
            <BarChart
              data={data}
              margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
              accessibilityLayer
            >
              <CartesianGrid
                vertical={false}
                stroke="var(--border)"
                strokeDasharray="3 5"
              />
              <XAxis
                dataKey="timestamp"
                tickLine={false}
                axisLine={false}
                minTickGap={32}
                tickMargin={12}
                tickFormatter={(timestamp) =>
                  formatXAxisLabel({ timestamp, granularity })
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={90}
                tickFormatter={(value: number) =>
                  value === 0
                    ? "$0"
                    : Math.abs(value) < 0.000001
                      ? `$${value.toExponential(1)}`
                      : `$${value.toLocaleString("en-US", { maximumFractionDigits: Math.abs(value) < 1 ? 6 : 2 })}`
                }
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) =>
                      `${new Date(String(value)).toLocaleString("en-US", { timeZone: "UTC", month: "short", day: "numeric", ...(granularity === "minute" || granularity === "hour" ? { hour: "numeric", minute: "2-digit" } : {}) })} UTC`
                    }
                    formatter={(_value, _name, item) => (
                      <div className="endpoint-tooltip">
                        <span>Settled usage</span>
                        <strong>
                          {formatAtoms(item.payload.amountAtoms, "usd")}
                        </strong>
                      </div>
                    )}
                  />
                }
              />
              <Bar
                dataKey="amountUsd"
                fill="var(--chart-1)"
                radius={[3, 3, 0, 0]}
                maxBarSize={36}
                isAnimationActive={false}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className="analytics-chart-empty" role="status">
            {unavailable[spend?.reason ?? "not_supported"]}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
